/**
 * Company-bucket lifestyle stock for Pathfinder galleries.
 * Each property site gets a deterministic sample of STOCK_PICK_COUNT URLs,
 * sprinkled through the curated Community / All tour (never the Apartment tab).
 */

const COMPANY_MEDIA_BASE =
  'https://media.multihub.io/company/5742705921490944';

/** Full pool of company lifestyle stills. */
export const STOCK_LIFESTYLE_URLS: readonly string[] = [
  `${COMPANY_MEDIA_BASE}/01a11c95-3647-7077-ba57-840095ddf37a.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c94-ac69-7512-98d8-1fa900c0d7f3.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c93-ea4b-76ca-b87d-1e240699dd29.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c93-40ba-71aa-9a2a-a52cdb5d82c9.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c92-c6c5-762e-a795-98167a4a5d53.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c91-98b1-7048-81ab-1cac1a7c37a1.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c90-c322-777a-ad6f-8558a8e6c741.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c90-25c7-76ea-9a36-4e2ca027e6e0.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c8d-db41-7247-a49a-662e6ea21a97.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c8e-0bbf-74b7-b6f5-d0212a767124.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c8d-a3ba-718d-9392-9b481191a81f.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c8d-6c3e-710a-ab18-d98dcf893f1e.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c8d-3579-762b-a035-f0a09e9ef96e.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c8c-fc20-7004-9ff9-374e30890834.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c8c-bb1a-713f-be1d-10b8fa8df664.webp`,
  `${COMPANY_MEDIA_BASE}/01a11c8c-74b2-7528-ab71-a14415c44168.webp`,
];

export const STOCK_PICK_COUNT = 5;

export interface StockLifestylePhoto {
  image: string;
  title: string;
  alt_text: string;
  description: string;
  original_url?: string | null;
}

function hashSeed(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** Deterministic PRNG (Mulberry32). */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleInPlace<T>(items: T[], rng: () => number): void {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = items[i]!;
    items[i] = items[j]!;
    items[j] = tmp;
  }
}

/** Stable per-property sample from the company lifestyle pool. */
export function pickStockLifestyleUrls(
  seed: string,
  count: number = STOCK_PICK_COUNT,
): string[] {
  const take = Math.min(count, STOCK_LIFESTYLE_URLS.length);
  if (take <= 0) return [];
  const rng = mulberry32(hashSeed(seed || 'pathfinder-lifestyle'));
  const pool = [...STOCK_LIFESTYLE_URLS];
  shuffleInPlace(pool, rng);
  return pool.slice(0, take);
}

export function stockLifestylePhotos(seed: string, count?: number): StockLifestylePhoto[] {
  return pickStockLifestyleUrls(seed, count).map((image) => ({
    image,
    title: 'Lifestyle',
    alt_text: 'Lifestyle at the community',
    description: '',
    original_url: null,
  }));
}

export interface StockSlotOverride {
  image?: string | null;
  alt?: string | null;
}

/**
 * Seeded stock defaults, with per-slot editor overrides winning when set.
 * Empty / cleared slot fields fall back to the seeded company stock URL.
 */
export function resolveStockLifestylePhotos(
  seed: string,
  overrides: StockSlotOverride[] = [],
  count: number = STOCK_PICK_COUNT,
): Array<StockLifestylePhoto & { stockSlotIndex: number }> {
  return stockLifestylePhotos(seed, count).map((stock, index) => {
    const override = overrides[index];
    const image = (override?.image || '').trim() || stock.image;
    const alt = (override?.alt || '').trim() || stock.alt_text;
    return {
      ...stock,
      image,
      alt_text: alt,
      stockSlotIndex: index,
    };
  });
}

/** Score how patterned `p` looks against already-chosen stock indices. Higher is better. */
function placementScore(p: number, chosen: number[]): number {
  let score = 0;
  // Keep the opening row mostly property photography (4-col desktop lead).
  if (p < 4) score -= 20;
  for (const prev of chosen) {
    const gap = Math.abs(p - prev);
    if (gap < 2) return -Infinity;
    // Prefer breathing room, but not a rigid march.
    score += Math.min(gap, 8) * 4;
    if (gap >= 3 && gap <= 7) score += 6;

    for (const cols of [4, 3, 2] as const) {
      const col = p % cols;
      const prevCol = prev % cols;
      const row = Math.floor(p / cols);
      const prevRow = Math.floor(prev / cols);
      const dCol = Math.abs(col - prevCol);
      const dRow = Math.abs(row - prevRow);

      // Same column nearby → vertical stack.
      if (dCol === 0 && dRow <= 2) score -= cols * 24;
      else if (dCol === 0) score -= cols * 4;

      // Knight/diagonal steps → staircase across the grid.
      if (dCol > 0 && dCol === dRow && dRow <= 2) score -= cols * 22;
      // Constant step of ±1 column per row also reads as a diagonal.
      if (dRow > 0 && dCol === 1 && dRow <= 3) score -= cols * 10;
    }
  }
  return score;
}

/**
 * Choose absolute indices for stock tiles in a final list of `finalLen`.
 * Index 0 is reserved for the property lead/hero. Placement is seeded and
 * irregular — avoids adjacent tiles, vertical stacks, and clean diagonals
 * on 2/3/4-column gallery grids.
 */
export function chooseStockPositions(
  finalLen: number,
  stockCount: number,
  seed: string = 'pathfinder-lifestyle',
): number[] {
  if (stockCount <= 0 || finalLen <= 0) return [];
  if (finalLen === 1) return [];

  const take = Math.min(stockCount, finalLen - 1);
  const lo = 1;
  const hi = finalLen - 1;
  const span = hi - lo + 1;
  const rng = mulberry32(hashSeed(`sprinkle:${seed}:${finalLen}:${take}`));
  const positions: number[] = [];

  for (let i = 0; i < take; i++) {
    // Soft zones keep coverage across the gallery; jitter edges so cuts aren't uniform.
    const rawLo = lo + Math.floor((i * span) / take);
    const rawHi = lo + Math.floor(((i + 1) * span) / take) - 1;
    const jitter = span >= take * 3 ? Math.floor(rng() * 2) : 0;
    const zLo = Math.min(Math.max(rawLo - (i > 0 ? jitter : 0), lo), hi);
    const zHi = Math.max(zLo, Math.min(rawHi + (i < take - 1 ? jitter : 0), hi));

    const candidates: Array<{ p: number; score: number }> = [];
    for (let p = zLo; p <= zHi; p++) {
      const score = placementScore(p, positions);
      if (score === -Infinity) continue;
      // Seeded noise breaks ties so picks feel organic, not geometric.
      candidates.push({ p, score: score + rng() * 14 });
    }

    if (candidates.length === 0) {
      // Zone too constrained — fall back to any free index in range.
      for (let p = zLo; p <= zHi; p++) {
        if (!positions.includes(p) && (positions.length === 0 || Math.abs(p - positions[positions.length - 1]!) >= 2)) {
          candidates.push({ p, score: rng() });
        }
      }
    }
    if (candidates.length === 0) {
      for (let p = lo; p <= hi; p++) {
        if (!positions.includes(p)) candidates.push({ p, score: placementScore(p, positions) + rng() * 4 });
      }
    }

    candidates.sort((a, b) => b.score - a.score);
    // Weighted pick among the better half of the zone — avoids always taking the
    // single "optimal" cell that recreates diagonals/columns.
    const pool = candidates.slice(0, Math.max(1, Math.ceil(candidates.length / 2)));
    let weightSum = 0;
    const weights = pool.map((c) => {
      const w = Math.max(0.01, c.score + 50);
      weightSum += w;
      return w;
    });
    let pick = rng() * weightSum;
    let chosen = pool[0]!.p;
    for (let w = 0; w < pool.length; w++) {
      pick -= weights[w]!;
      if (pick <= 0) {
        chosen = pool[w]!.p;
        break;
      }
    }
    positions.push(chosen);
  }

  positions.sort((a, b) => a - b);

  // Final collision repair (should be rare).
  const used = new Set<number>();
  return positions.map((pos) => {
    let p = pos;
    while (used.has(p) && p < hi) p += 1;
    while (used.has(p) && p > lo) p -= 1;
    if (used.has(p)) {
      for (let q = lo; q <= hi; q++) {
        if (!used.has(q)) {
          p = q;
          break;
        }
      }
    }
    used.add(p);
    return p;
  });
}

/**
 * Insert `stock` through `photos` with seeded, irregular spacing.
 * Never displaces index 0 (hero / lead). Existing sources win on URL match.
 */
export function sprinkleStockPhotos<T extends { image?: string | null; original_url?: string | null }>(
  photos: T[],
  stock: T[],
  seed: string = 'pathfinder-lifestyle',
): T[] {
  if (stock.length === 0) return photos;

  const existing = new Set(
    photos.map((photo) => photo.image || photo.original_url || '').filter(Boolean),
  );
  const fresh = stock.filter((photo) => {
    const src = photo.image || photo.original_url || '';
    return src && !existing.has(src);
  });
  if (fresh.length === 0) return photos;
  if (photos.length === 0) return fresh;

  const finalLen = photos.length + fresh.length;
  const positionList = chooseStockPositions(finalLen, fresh.length, seed);
  const stockAt = new Map<number, T>();
  positionList.forEach((pos, i) => {
    if (i < fresh.length) stockAt.set(pos, fresh[i]!);
  });

  const merged: T[] = [];
  let propIdx = 0;
  for (let i = 0; i < finalLen; i++) {
    const stockPhoto = stockAt.get(i);
    if (stockPhoto) {
      merged.push(stockPhoto);
    } else if (propIdx < photos.length) {
      merged.push(photos[propIdx++]!);
    }
  }
  return merged;
}
